import * as React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { changeOrderStatus } from "../slices/authSlice";
import { useAppSelector, useAppDispatch } from "../hooks/redux-hooks";
import { getCustomerCurOrders } from "../slices/authSlice";
import { serviceType, orderStatus } from "../constants";
import { getOrderStatusText } from "../utils/orderUtils";
import { getLocalizedErrorMessage } from "../utils/errorUtils";
import { showNotification, NotificationType } from "../slices/notificationSlice";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// 配置 dayjs 使用时区
dayjs.extend(utc);
dayjs.extend(timezone);

const CustomerOrderList = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const basicUserInfo = useAppSelector((state) => state.auth.basicUserInfo);
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<any[]>([]);

  const handleViewDetails = (order: any) => {
    // 跳转到 CurrentOrder 页面，并传递订单信息
    navigate("/curorder", { state: { orderDetail: order } });
  };

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmingOrder, setConfirmingOrder] = useState<any>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelingOrder, setCancelingOrder] = useState<any>(null);
  const [isStatusChanging, setIsStatusChanging] = useState(false);

  const handleConfirmOrder = (order: any) => {
    setConfirmingOrder(order);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false);
    setConfirmingOrder(null);
  };

  const handleConfirmSubmit = async () => {
    if (!confirmingOrder || isStatusChanging) return;

    try {
      setIsStatusChanging(true);
      await dispatch(
        changeOrderStatus({
          order_id: confirmingOrder.order_id,
          status: orderStatus.CONFIRMED,
          isClient: true,
        })
      ).unwrap();

      // 更新本地订单列表
      setOrders(
        orders.map((order) =>
          order.order_id === confirmingOrder.order_id
            ? { ...order, status: orderStatus.CONFIRMED }
            : order
        )
      );

      handleConfirmDialogClose();
    } catch (error: any) {
      const errMsg = error?.message;
      dispatch(
        showNotification({
          message: t('msgConfirmOrderError') + errMsg,
          type: NotificationType.Error,
        })
      );
      // console.error("确认订单失败:", error);
    } finally {
      setIsStatusChanging(false);
    }
  };

  const handleCancelOrder = (order: any) => {
    setCancelingOrder(order);
    setCancelDialogOpen(true);
  };

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
    setCancelingOrder(null);
  };

  const handleCancelSubmit = async () => {
    if (!cancelingOrder || isStatusChanging) return;

    try {
      // await dispatch(
      //   changeOrderStatus({
      //     order_id: cancelingOrder.order_id,
      //     status: orderStatus.CANCELED,
      //     isClient: true,
      //   })
      // );
      setIsStatusChanging(true);
      await dispatch(
        changeOrderStatus({
          order_id: cancelingOrder.order_id,
          status: orderStatus.CANCELED,
          isClient: true,
        })
      ).unwrap();

      // 更新本地订单列表
      setOrders(
        orders.map((order) =>
          order.order_id === cancelingOrder.order_id
            ? { ...order, status: orderStatus.CANCELED }
            : order
        )
      );

      handleCancelDialogClose();
    } catch (error: any) {
      const errorMessage = getLocalizedErrorMessage(error, error?.message);
      dispatch(
        showNotification({
          message: errorMessage,
          type: NotificationType.Error,
        })
      );
      // console.error("取消订单失败:", error);
    } finally {
      setIsStatusChanging(false);
    }
  };

  useEffect(() => {
    if (basicUserInfo?.uid) {
      setLoading(true);
      dispatch(
        getCustomerCurOrders({
          customer_id: +basicUserInfo.uid,
          count: 50,
          needComments: false,
        }) as any
      )
        .unwrap()
        .then((result: any) => {
          // console.log('result', result);
          if (result) {
            setOrders(result);
          }
          setLoading(false);
        })
        .catch((error: any) => {
          const errMsg = error?.message;
          dispatch(
            showNotification({
              message: t('msgLoadOrdersError') + errMsg,
              type: NotificationType.Error,
            })
          );
          setLoading(false);
        });
    }
  }, [dispatch, basicUserInfo]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (orders.length === 0) {
    return (
      <Typography variant="body1" align="center" sx={{ mt: 4 }}>
        {t("labelNoOrders")}
      </Typography>
    );
  }

  return (
    <Box>
      {orders.map((order) => (
        <Card key={order.order_id || order.id} sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {order.service_type === serviceType.AIRPORT_PICK_UP
                ? t("pickupService")
                : t("dropoffService")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("orderId")}: {order.order_id}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("labelOrderDate")}:{" "}
              {new Date(order.created_at).toLocaleString()}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body1">
              {t("status")}:
              <Typography
                component="span"
                color={
                  order.status === orderStatus.CANCELED ? "error" : "inherit"
                }
              >
                {getOrderStatusText(order.status, t)}
              </Typography>
              {order.status === orderStatus.REVIEWED && (
                <>
                  <Typography component="span" color="error" sx={{ ml: 1 }}>
                    {t("pleaseConfirmOrderPrompt")}
                  </Typography>
                  <Typography
                    dangerouslySetInnerHTML={{
                      __html: t("pleaseConfirmOrderDetail"),
                    }}
                    sx={{ mb: 2 }}
                  />
                </>
              )}
            </Typography>
            <Typography variant="body1">
              {t("name")}: {order.customer_name}
            </Typography>
            {order.service_type === serviceType.AIRPORT_PICK_UP ? (
              <>
                <Typography variant="body1">
                  {/* {t('flightArrivalTime')}: {new Date(order.flight_time).toLocaleString()} */}
                  {t("flightArrivalTime")}:{" "}
                  {dayjs(order.flight_time)
                    .tz("America/Los_Angeles")
                    .format("YYYY-MM-DD HH:mm")}
                </Typography>
                <Typography variant="body1">
                  {t("pickupAirport")}: {order.airport}
                </Typography>
                <Typography variant="body1">
                  {t("dropOffLocation")}: {order.addr1}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body1">
                  {/* {t('useTime')}: {new Date(order.use_time).toLocaleString()} */}
                  {t("useDateTime")}:{" "}
                  {dayjs(order.use_time)
                    .tz("America/Los_Angeles")
                    .format("YYYY-MM-DD HH:mm")}
                </Typography>
                <Typography variant="body1">
                  {t("pickupLocation")}: {order.addr1}
                </Typography>
                <Typography variant="body1">
                  {t("dropOffAirport")}: {order.airport}
                </Typography>
              </>
            )}
            {order.status === orderStatus.ASSIGNED && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography
                    variant="body1"
                    sx={{ color: "primary.main", fontWeight: "bold", mb: 1 }}
                  >
                    {t("labelAssignedDriverInfo")}
                  </Typography>
                  <Typography variant="body2">
                    {t("labelDriverName")}: {order.driver_name}
                  </Typography>
                  <Typography variant="body2">
                    {t("labelDriverPhone")}: {order.driver_phone}
                  </Typography>
                  <Typography variant="body2">
                    {t("licensePlate")}: {order.licence_plate}
                  </Typography>
                  <Typography variant="body2">
                    {t("carModel")}: {order.car_model}
                  </Typography>
                  <Typography variant="body2">
                    {t("seats")}: {order.seat}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t("driverDetailsLink")}</strong>{" "}
                    {order.avatar ? (
                      <a href={order.avatar} target="_blank" rel="noreferrer">
                        {order.avatar}
                      </a>
                    ) : (
                      t("toBeUpdated")
                    )}
                  </Typography>
                </Box>
              </>
            )}
          </CardContent>
          <Divider sx={{ my: 1 }} />
          <CardActions>
            {order.status != orderStatus.CANCELED && (
              <>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handleViewDetails(order)}
                >
                  {t("viewDetails")}
                </Button>
              </>
            )}
            {(order.status === orderStatus.INIT
              // || order.status === orderStatus.REVIEWED
            ) && (
                <>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() =>
                      navigate(
                        order.service_type === serviceType.AIRPORT_PICK_UP
                          ? "/editpickup"
                          : "/editdropoff",
                        { state: { orderInfo: order } }
                      )
                    }
                  >
                    {t("editOrder")}
                  </Button>
                </>
              )}
            {order.status === orderStatus.REVIEWED && (
              <>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handleConfirmOrder(order)}
                >
                  {t("confirmOrder")}
                </Button>
              </>
            )}
            {![orderStatus.CANCELED, orderStatus.COMPLETED].includes(
              order.status
            ) && (
                <>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => handleCancelOrder(order)}
                  >
                    {t("btnCancel")}
                  </Button>
                </>
              )}
          </CardActions>
        </Card>
      ))}
      <Dialog open={confirmDialogOpen} onClose={handleConfirmDialogClose}>
        <DialogTitle>{t("confirmOrder")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("confirmOrderPromptPart1")} <br />
            {t("confirmOrderPromptPart2")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmDialogClose} disabled={isStatusChanging}>{t("btnCancel")}</Button>
          <Button
            onClick={handleConfirmSubmit}
            variant="contained"
            color="primary"
            disabled={isStatusChanging}
          >
            {t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelDialogOpen} onClose={handleCancelDialogClose}>
        <DialogTitle>{t("btnCancelOrder")}</DialogTitle>
        <DialogContent>
          <Typography>{t("cancelOrderPrompt")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDialogClose} disabled={isStatusChanging}>{t("btnCancel")}</Button>
          <Button
            onClick={handleCancelSubmit}
            variant="contained"
            color="primary"
            disabled={isStatusChanging}
          >
            {t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerOrderList;
